/**
 * @file Combobox 可搜索单选
 * @description 输入过滤 + 自定义列表；Portal + 视口边界 + fade；列表内 ScrollArea。
 *
 * @whenToUse 选项较多需本地过滤的单选。
 * @whenNotToUse 少量固定枚举（用 Select）；多选（用 MultiSelect）。
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  searchControlWidth,
  useSearchForm,
} from '@/components/ui/search-form-context.tsx'

export type ComboboxOption = {
  /** 选项值 */
  value: string
  /** 展示文案 */
  label: string
  /** 可选禁用 */
  disabled?: boolean
}

export type ComboboxProps = {
  options: ComboboxOption[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  /** 搜索框占位 */
  searchPlaceholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
  /** 无匹配时的空态 */
  emptyText?: ReactNode
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = '请选择',
  searchPlaceholder = '搜索…',
  disabled = false,
  invalid = false,
  className,
  id,
  emptyText = '无匹配结果',
}: ComboboxProps) {
  const inSearchForm = useSearchForm()
  const listId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)
  const [query, setQuery] = useState('')

  const selected = options.find((item) => item.value === value)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((item) => item.label.toLowerCase().includes(q))
  }, [options, query])

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect()))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      setQuery('')
      updateBox()
      const frame = requestAnimationFrame(() => {
        setVisible(true)
        inputRef.current?.focus()
      })
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className={cn(
          'inline-flex h-8 items-center justify-between gap-1.5 rounded-lg border bg-raised px-2 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          searchControlWidth(inSearchForm),
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            selected ? 'text-foreground' : 'text-muted',
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <div className="border-b border-border p-2">
                <input
                  ref={inputRef}
                  value={query}
                  placeholder={searchPlaceholder}
                  className="h-8 w-full rounded-md border border-border bg-raised px-2 text-sm text-foreground"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <ScrollArea
                className="w-full"
                style={{
                  height: Math.min(
                    box.maxHeight - 56,
                    Math.max(filtered.length, 1) * 38 + 8,
                  ),
                }}
              >
                <div id={listId} role="listbox" className="p-1">
                  {filtered.length === 0 ? (
                    <p className="px-2 py-2 text-center text-sm text-muted">
                      {emptyText}
                    </p>
                  ) : (
                    filtered.map((option) => {
                      const isActive = option.value === value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          disabled={option.disabled}
                          className={cn(
                            'flex h-8 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
                            isActive ? 'text-primary' : 'text-foreground',
                          )}
                          onClick={() => {
                            if (option.disabled) return
                            onChange?.(option.value)
                            setOpen(false)
                          }}
                        >
                          <span className="min-w-0 truncate">{option.label}</span>
                          {isActive ? (
                            <Check className="size-3.5 shrink-0" aria-hidden />
                          ) : null}
                        </button>
                      )
                    })
                  )}
                </div>
              </ScrollArea>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
